import React, { useEffect, useMemo, useState } from 'react';
import { ExplainMetricButton } from '../../features/abf/copilot/AbfCopilotLayout';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Briefcase,
  RefreshCw,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Database,
  FileSearch,
  FileText,
  FileWarning,
  Gavel,
  Landmark,
  ListChecks,
  List,
  ShieldAlert,
  Ticket,
} from 'lucide-react';
import LoanFacilityCardDrawer, { parseLocalDate } from './LoanFacilityCardDrawer';
import {
  ActionBar,
  ControlRoomDrawer,
  DataTable,
  DefColumns,
  DefGroup,
  DefGroupTitle,
  DefLabel,
  DefRow,
  DefValue,
  DrawerBackdrop,
  DrawerHeader,
  DrawerTab,
  DrawerTabs,
  EmptyStateBox,
  EvidenceChip,
  MetricCard,
  MetricLabel,
  MetricStrip,
  MetricSubtext,
  MetricValue,
  ModalActions,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RowSubtext,
  RowTitle,
  StatusPill,
  TableShell,
  WorkspaceButton,
  WorkspacePanel,
  moneyCompact,
  moneyFull,
  numberCompact,
  percent,
  type Tone,
} from './shared/CreditWorkspace';
import { axiomTheme, toneAlpha } from '../../styles/designTokens';
import {
  blotterLoanPath,
  creditLoanReferencePath,
  iborPositionPath,
  leveragedLoanTicketPath,
  leveragedLoanTicketState,
  loanListPath,
  privateCreditTicketPath,
  securityMasterPath,
  agreementReviewPath,
} from '../../utils/loanNavigation';
import { CreditActionModal, CreditActionKind, CreditPositionLite } from './CreditLoanActionModals';
import { CORE_CREDIT_LIFECYCLE_ACTIONS, CreditLifecycleActionButton } from './CreditLifecycleActions';
import CreditFacilityPage from './CreditFacilityPage';
import FacilityShell, { type ShellFact } from '../../features/privateCredit/FacilityShell';
import FacilityAttentionRail from '../../features/privateCredit/FacilityAttentionRail';
import { getPcAttention } from '../../features/privateCredit/api';
import { PC_QUEUE_PATH } from '../../features/privateCredit/routes';
import ServicingWorkspace from '../../features/privateCredit/ServicingWorkspace';
import AgreementWorkspace from '../../features/privateCredit/AgreementWorkspace';
import DocumentsWorkspace from '../../features/privateCredit/DocumentsWorkspace';
import ActivityWorkspace from '../../features/privateCredit/ActivityWorkspace';
import { findAgreementJobForLoan, getAcquisitionTicket, type AcquisitionTicket } from '../../features/credit/agreementReview/agreementReviewApi';
import FacilityJourneyBar from '../../features/credit/facility/FacilityJourneyBar';
import { leveragedLoanJourney, privateCreditJourney, type NextStepTarget } from '../../features/credit/facility/facilityJourney';
import { plainCode } from '../../features/credit/plainWords';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import { useBusinessDate } from '../../contexts/BusinessDateContext';
import LoanOverviewWorkspace from './shared/LoanOverviewWorkspace';
import CreditLoanOperatingTabs from './CreditLoanOperatingTabs';

type LoanSource = 'LEVERAGED_LOAN' | 'PRIVATE_CREDIT';
export type DrawerTabKey = 'overview' | 'agreement' | 'servicing' | 'settlement' | 'underwriting' | 'covenants' | 'lme' | 'workout' | 'exceptions' | 'documents' | 'activity';

interface LoanLike {
  id: number | string;
  source: LoanSource;
  loanId?: string;
  cusip?: string;
  loanName?: string;
  borrowerName?: string;
  sponsorName?: string;
  facilityType?: string;
  seniority?: string;
  status?: string;
  currentCommitment?: number;
  originalCommitment?: number;
  fundedAmount?: number;
  unfundedAmount?: number;
  allInRate?: number;
  spreadBps?: number;
  baseRate?: string;
  floorRate?: number;
  oidPrice?: number;
  upfrontFeeBps?: number;
  maturityDate?: string;
  closingDate?: string;
  sector?: string;
  industry?: string;
  spRating?: string;
  moodysRating?: string;
  fitchRating?: string;
  pikToggle?: string;
  pikElectionCurrent?: string;
  pikSpreadBps?: number;
  ddtlCommitment?: number;
  ddtlFunded?: number;
  ddtlUndrawn?: number;
  ddtlExpiry?: string;
  /** A pricing change the facility carries for a future date (recorded on the loan). */
  pendingPricingEffectiveDate?: string;
  pendingSpreadBps?: number;
  currentCapacity?: number;
  arrangementId?: number;
  facilityCode?: string;
  commitmentCurrency?: string;
  originalFacilityCapacity?: number;
  currentFacilityCapacity?: number;
  capacityAsOf?: string;
  capacitySource?: string;
  facilityState?: string;
  availabilityStartDate?: string;
  availabilityEndDate?: string;
  arrangement?: {
    id?: number;
    arrangementCode?: string;
    arrangementName?: string;
    arrangementStatus?: string;
    agreementDate?: string;
    governingLaw?: string;
    currency?: string;
    facilities?: Array<{
      id?: number; facilityCode?: string; loanName?: string; facilityType?: string;
      facilityState?: string; commitmentCurrency?: string; currentFacilityCapacity?: number;
      globalOutstanding?: number; maturityDate?: string;
    }>;
    relationships?: Array<{
      id?: number; fromFacilityCode?: string; toFacilityCode?: string;
      relationshipType?: string; relationshipScope?: string; relationshipStatus?: string;
    }>;
  };
  globalOutstanding?: number;
  globalAvailability?: number;
  tickingFeeBps?: number;
  commitmentFeeBps?: number;
  csaBps?: number;
  stepDownSpreadBps?: number;
  stepDownTriggerLeverage?: number;
  securityId?: number;
  portfolioId?: number;
  security?: { id?: number; cusip?: string; isin?: string };
}

interface CovenantRow {
  id?: number;
  covenantName?: string;
  borrowerName?: string;
  scope?: string;
  covenantType?: string;
  covenantCategory?: string;
  metricType?: string;
  thresholdType?: string;
  thresholdValue?: number;
  thresholdValueMax?: number;
  warningThresholdPct?: number;
  measurementFrequency?: string;
  hasStepSchedule?: boolean;
  hasEquityCure?: boolean;
  maxEquityCures?: number;
  curePeriodDays?: number;
  effectiveDate?: string;
  terminationDate?: string;
  amendedBy?: string;
  amendedAt?: string;
  currentStatus?: string;
  latestActualValue?: number;
  actualValue?: number;
  latestCushionPct?: number;
  /** The threshold the latest test was run against, which an amendment can make differ from today's. */
  latestThresholdValue?: number;
  cushionPct?: number;
  latestTestDate?: string;
  latestPeriodEndDate?: string;
  auditorValidated?: boolean;
}

interface CovenantAlertRow {
  alertType?: string;
  message?: string;
  covenant?: {
    id?: number;
    covenantName?: string;
    borrowerName?: string;
  };
}

interface WatchlistEntry {
  loanId?: number;
  borrowerName?: string;
  loanIdentifier?: string;
  sector?: string;
  currentExposure?: number;
  riskScore?: number;
  severity?: string;
  recommendedSituationType?: string;
  signals?: Array<{ signalType?: string; detail?: string; severity?: string }>;
}

interface DistressedSituation {
  id?: number;
  companyName?: string;
  situationType?: string;
  status?: string;
  totalExposure?: number;
  ourExposure?: number;
  estimatedRecovery?: number;
  industry?: string;
  nextMilestoneDate?: string;
  nextDeadline?: string;
  deadlineDescription?: string;
  probabilityOfDefault?: number;
}

interface TradeRow {
  id?: number;
  ticketType?: string;
  eventType?: string;
  status?: string;
  securityId?: number;
  securityName?: string;
  portfolioName?: string;
  quantity?: number;
  amount?: number;
  tradeDate?: string;
}

interface LifecycleEvent {
  id?: number;
  eventType?: string;
  eventDate?: string;
  eventAmount?: number;
  baseRate?: number;
  status?: string;
  message?: string;
  sourceDocumentName?: string;
  sourceDocumentType?: string;
  sourceJobId?: string;
  createdBy?: string;
  createdAt?: string;
  portfolioCode?: string;
}

interface OperatingWorkspace {
  loan?: LoanLike;
  underwritingPacket?: any;
  underwritingEvents?: any[];
  lmeAssessment?: any;
  lmeHistory?: any[];
  workoutCase?: any;
  recoveryScenarios?: any[];
  amendmentProposals?: any[];
  covenants?: CovenantRow[];
  covenantEvidence?: any[];
  covenantCureUsage?: any[];
  exceptions?: any[];
  auditTrail?: any[];
  documentReferenceLinks?: Array<{
    jobId?: string; documentName?: string; documentRole?: string; reviewer?: string; reviewedAt?: string;
  }>;
}

interface LoanAccrualRow {
  id?: number;
  accrualDate?: string;
  cashInterestAccrual?: number;
  oidAmortization?: number;
  commitmentFeeAccrual?: number;
  tickingFeeAccrual?: number;
  pikAccrual?: number;
  totalAccrual?: number;
  allInRate?: number;
  currentFace?: number;
  carryingValue?: number;
  createdAt?: string;
}

interface Props {
  loan: LoanLike;
  covenants: CovenantRow[];
  covenantAlerts: CovenantAlertRow[];
  watchlist: WatchlistEntry[];
  distressed: DistressedSituation[];
  trades: TradeRow[];
  onClose: () => void;
  onNavigate: (
    tab: 'loans' | 'covenants' | 'distressed' | 'exposure',
    context?: Record<string, string | number | undefined>
  ) => void;
  // Tab to open on first render — used when the drawer is reached via a deep-link
  // (e.g. Covenant Center → Open in Loan 360 lands on the Covenants tab).
  defaultTab?: DrawerTabKey;
  presentation?: 'drawer' | 'page';
  /** Called after an action that can change covenant alerts, trades or watchlist entries. */
  onDataChanged?: () => void;
  /** The loan page keeps the tab in its address; without it the tab is a query parameter. */
  onTabChange?: (tab: DrawerTabKey) => void;
}

/* Control-room shells shared with AbfFacilityDrawer's visual language:
   an amber work-queue banner, an emerald all-clear banner, and the
   walk rows used for the commitment-utilization readout. */
const QueueShell = styled.div`
  border: 0;
  background: transparent;
  padding: 0.4rem 0;
  margin-top: 0.8rem;
`;

const AllClearShell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 0;
  background: transparent;
  color: #6ee7b7;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.4rem 0;
  margin-top: 0.8rem;

  svg { width: 15px; height: 15px; flex: none; }
`;

const WalkShell = styled.div`
  border: 0;
  border-top: 1px solid #1e293b;
  background: transparent;
  padding: 20px 0 0;
  margin-top: 0;
`;

const WalkRow = styled.div`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 140px;
  align-items: center;
  gap: 0.8rem;
  padding: 0.28rem 0;

  @media (max-width: 700px) {
    grid-template-columns: 110px minmax(0, 1fr) 110px;
  }
`;

const WalkLabel = styled.div<{ $emphasis?: boolean }>`
  color: ${p => (p.$emphasis ? '#f8fafc' : '#94a3b8')};
  font-size: 0.74rem;
  font-weight: ${p => (p.$emphasis ? 850 : 600)};
`;

const WalkTrack = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.08);
  overflow: hidden;
`;

const WalkFill = styled.div<{ $pct: number; $tone: 'cyan' | 'emerald' | 'amber' | 'slate' | 'violet' }>`
  height: 100%;
  width: ${p => Math.max(0, Math.min(100, p.$pct))}%;
  border-radius: 999px;
  background: ${p => ({
    cyan: 'linear-gradient(90deg, rgba(56,189,248,0.85), rgba(14,165,233,0.55))',
    emerald: 'linear-gradient(90deg, rgba(52,211,153,0.85), rgba(16,185,129,0.55))',
    amber: 'linear-gradient(90deg, rgba(251,191,36,0.8), rgba(217,119,6,0.5))',
    violet: 'linear-gradient(90deg, rgba(167,139,250,0.8), rgba(124,58,237,0.5))',
    slate: 'rgba(148, 163, 184, 0.35)',
  }[p.$tone])};
  transition: width 0.4s ease;
`;

const WalkValue = styled.div<{ $tone?: 'deduct' | 'result' }>`
  color: ${p => (p.$tone === 'deduct' ? '#fbbf24' : p.$tone === 'result' ? '#7dd3fc' : '#e2e8f0')};
  font-size: 0.76rem;
  font-weight: 800;
  text-align: right;
  white-space: nowrap;
`;





const AttestationPanel = styled.div`
  margin-top: 0.75rem;
  border-top: 1px solid ${axiomTheme.color.border.subtle};
  padding-top: 0.75rem;
  display: grid;
  gap: 0.55rem;
`;

const AttestationToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.76rem;
  font-weight: 800;

  input {
    width: 15px;
    height: 15px;
    accent-color: ${axiomTheme.color.tone.cyan};
  }
`;

const AttestationInput = styled.input`
  width: 100%;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.42);
  color: ${axiomTheme.color.text.primary};
  font-size: 0.78rem;
  padding: 0.62rem 0.7rem;
  outline: none;

  &:focus {
    border-color: ${axiomTheme.color.border.focus};
  }

  &::placeholder {
    color: ${axiomTheme.color.text.muted};
  }
`;


const Driver = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '36')};
  border-radius: 9px;
  background: rgba(2, 6, 23, 0.34);
  padding: 0.72rem 0.8rem;
`;

const DriverHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
`;

const DriverTitle = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-weight: 800;
  font-size: 0.8rem;
`;

const DriverText = styled.div`
  color: ${axiomTheme.color.text.muted};
  margin-top: 0.22rem;
  font-size: 0.72rem;
  line-height: 1.4;
`;


const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg {
    color: ${axiomTheme.color.tone.cyan};
    width: 18px;
    height: 18px;
  }
`;

const asNumber = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalize = (value?: string | null) => (value || '').trim().toLowerCase();

const sourceLabel = (source: LoanSource) => source === 'PRIVATE_CREDIT' ? 'Private Credit' : 'Syndicated Loan';

const loanDisplayName = (loan: LoanLike) =>
  loan.borrowerName || loan.loanName || loan.loanId || `${sourceLabel(loan.source)} #${loan.id}`;

const securityIdOf = (loan: LoanLike) => loan.securityId ?? loan.security?.id;

const statusTone = (status?: string): Tone => {
  const s = (status || '').toUpperCase();
  if (s.includes('BREACH') || s.includes('DEFAULT') || s.includes('FAILED') || s.includes('CRITICAL')) return 'red';
  if (s.includes('WATCH') || s.includes('WARN') || s.includes('PENDING') || s.includes('DRAFT')) return 'amber';
  if (s.includes('ACTIVE') || s.includes('PERFORMING') || s.includes('POSTED') || s.includes('APPLIED')) return 'emerald';
  if (s.includes('APPROVED') || s.includes('EXECUTED')) return 'cyan';
  return 'slate';
};

// LME severity bands MUST match the backend (CreditLoanOperatingService.computeLmeScore /
// LME_RULES_V1): score >= 80 = CRITICAL, >= 60 = HIGH, >= 35 = MODERATE, else LOW. The
// previous client-side band (>= 70 = High, >= 35 = Moderate) diverged from the backend and
// rendered a persisted score of 65 (severity HIGH) as "Moderate". Always prefer the
// persisted `severity` field; only derive from the numeric score when no severity is given.
const lmeSeverityFromScore = (score: number): string =>
  score >= 80 ? 'CRITICAL' : score >= 60 ? 'HIGH' : score >= 35 ? 'MODERATE' : 'LOW';

const lmeSeverityTone = (severity?: string): Tone => {
  switch ((severity || '').toUpperCase()) {
    case 'CRITICAL':
      return 'red';
    case 'HIGH':
      return 'red';
    case 'MODERATE':
      return 'amber';
    case 'LOW':
      return 'emerald';
    default:
      return 'slate';
  }
};


const hasText = (value: any) => typeof value === 'string' && value.trim().length > 0;

/** Whole days from one ISO date to another; null when either is missing. */
const daysBetween = (from?: string | null, to?: string | null): number | null => {
  const start = parseLocalDate(from || undefined);
  const end = parseLocalDate(to || undefined);
  return start && end ? Math.round((end.getTime() - start.getTime()) / 86400000) : null;
};

const fmtDate = (date?: string) => {
  const parsed = parseLocalDate(date);
  return parsed ? parsed.toLocaleDateString() : '--';
};

const parseJsonArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  const raw = typeof value === 'string' ? value : value?.value;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const documentEvidenceDoc = (documents: any[]) =>
  documents.find(d => normalize(d?.label) === 'document evidence trail');

const openTrade = (trade: TradeRow) => {
  const s = (trade.status || '').toUpperCase();
  const ticketType = (trade.ticketType || '').toUpperCase();
  return ['PRIVATE_CREDIT', 'LEVERAGED_LOAN'].includes(ticketType) && !['POSTED', 'CANCELLED', 'REJECTED'].includes(s);
};

const matchByBorrower = (borrower?: string, candidate?: string) => {
  const a = normalize(borrower);
  const b = normalize(candidate);
  return Boolean(a && b && (a === b || a.includes(b) || b.includes(a)));
};

const covenantAppliesToLoan = (loan: LoanLike, covenant: CovenantRow) => {
  if (matchByBorrower(loan.borrowerName, covenant.borrowerName)) return true;
  const expectedScope = loan.source === 'PRIVATE_CREDIT' ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN';
  return covenant.scope === expectedScope && matchByBorrower(loan.loanName, covenant.borrowerName);
};

const tradeAppliesToLoan = (loan: LoanLike, trade: TradeRow) => {
  if (!openTrade(trade)) return false;
  const sid = securityIdOf(loan);
  if (sid != null && trade.securityId === sid) return true;
  const text = normalize(`${trade.securityName || ''} ${trade.portfolioName || ''}`);
  return Boolean(text && [loan.borrowerName, loan.loanName, loan.loanId].some(v => normalize(v) && text.includes(normalize(v))));
};

const buildUnderwritingChecks = (
  loan: LoanLike,
  covenants: CovenantRow[],
  events: LifecycleEvent[],
  referenceLinks: OperatingWorkspace['documentReferenceLinks'] = [],
  // The approved agreement the loan was imported from is itself document evidence (F-PAR-005).
  sourceAgreementJobId: string | null = null,
) => [
  {
    label: 'Borrower identity',
    ok: hasText(loan.borrowerName) && hasText(loan.loanId),
    detail: hasText(loan.borrowerName) ? `${loan.borrowerName} · ${loan.loanId || 'loan id pending'}` : 'Borrower and facility id missing',
  },
  {
    label: 'Sponsor / sector context',
    ok: loan.source === 'LEVERAGED_LOAN' ? hasText(loan.sector) : hasText(loan.sponsorName) && hasText(loan.sector),
    detail: `${loan.sponsorName || 'sponsor not mapped'} · ${loan.sector || 'sector not mapped'}`,
  },
  {
    label: 'Economics',
    ok: asNumber(loan.currentCommitment ?? loan.originalCommitment) > 0 && (asNumber(loan.allInRate) > 0 || asNumber(loan.spreadBps) > 0),
    detail: `${moneyCompact(loan.currentCommitment ?? loan.originalCommitment)} commitment · ${loan.allInRate ? percent(loan.allInRate) : `${loan.spreadBps || '--'} bps spread`}`,
  },
  {
    label: 'Maturity and facility type',
    ok: hasText(loan.facilityType) && hasText(loan.maturityDate),
    detail: `${plainCode(loan.facilityType) || 'facility missing'} · matures ${fmtDate(loan.maturityDate)}`,
  },
  {
    label: 'Ratings / private risk grade',
    ok: hasText(loan.spRating) || hasText(loan.moodysRating) || hasText(loan.fitchRating),
    detail: `S&P ${loan.spRating || '--'} · Moody's ${loan.moodysRating || '--'} · Fitch ${loan.fitchRating || '--'}`,
  },
  {
    label: 'Covenant package',
    ok: covenants.length > 0,
    detail: `${numberCompact(covenants.length)} covenant record${covenants.length === 1 ? '' : 's'} linked by borrower`,
  },
  {
    label: 'Document evidence trail',
    ok: referenceLinks.length > 0 || Boolean(sourceAgreementJobId)
      || events.some(e => hasText(e.sourceDocumentName) && hasText(e.sourceJobId)),
    detail: referenceLinks.length > 0
      ? `${referenceLinks.length} approved reference-document link${referenceLinks.length === 1 ? '' : 's'} persisted`
      : sourceAgreementJobId
      ? `Created from the approved credit agreement (document job ${sourceAgreementJobId.slice(0, 8)})`
      : events.some(e => hasText(e.sourceDocumentName) && hasText(e.sourceJobId))
      ? 'Lifecycle events include linked ingestion documents'
      : 'No verified document-ingestion lineage on lifecycle events yet',
  },
];

// BUG-020: The previous client-side buildLmeDrivers computed a *preview* score and a set
// of made-up drivers that diverged from the backend-persisted assessment (LME_RULES_V1),
// producing a different number/severity than the governed score. To prevent the
// triple-divergence (preview != persisted != displayed band), no client-side score is
// computed anymore. Until the backend assessment is run, we surface an explicit
// "Run Score to compute" placeholder rather than a fabricated number.
const lmeNotScoredDrivers: Array<{ title: string; detail: string; points: number; severity: string; tone: Tone }> = [
  {
    title: 'Run Score to compute',
    detail: 'No governed LME assessment has been scored for this asset yet. Use "Run Score" to generate the LME_RULES_V1 score, drivers, and severity from the backend.',
    points: 0,
    severity: 'NONE',
    tone: 'slate',
  },
];

const OverviewPlaceholder = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-bottom: 1rem;
  div {
    border-radius: 12px;
    background: linear-gradient(90deg, rgba(30, 41, 59, 0.55), rgba(51, 65, 85, 0.35), rgba(30, 41, 59, 0.55));
    background-size: 200% 100%;
    animation: overview-placeholder 1.4s ease-in-out infinite;
  }
  .journey { height: 74px; }
  .step { height: 76px; }
  .actions { height: 52px; width: 70%; }
  .panel { height: 320px; }
  @keyframes overview-placeholder { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
  @media (prefers-reduced-motion: reduce) { div { animation: none; } }
`;

const Loan360Drawer: React.FC<Props> = ({
  loan: selectedLoan,
  covenants,
  covenantAlerts,
  watchlist,
  distressed,
  trades,
  onClose,
  onNavigate,
  defaultTab,
  presentation = 'drawer',
  onDataChanged,
  onTabChange,
}) => {
  const isFacilityPage = presentation === 'page';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<DrawerTabKey>(defaultTab === 'settlement' ? 'overview' : defaultTab ?? 'overview');
  const [events, setEvents] = useState<LifecycleEvent[]>([]);
  const [agreementJobId, setAgreementJobId] = useState<string | null>(null);
  // undefined while it is being read; null when the facility has no acquisition ticket.
  const [acquisitionTicket, setAcquisitionTicket] = useState<AcquisitionTicket | null | undefined>(undefined);
  const { config: tenantConfig } = useTenantConfig();
  const { businessDate } = useBusinessDate();
  const [exceptionSection, setExceptionSection] = useState<string | undefined>(undefined);
  const [loanCard, setLoanCard] = useState<any | null | undefined>(undefined);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [opsWorkspace, setOpsWorkspace] = useState<OperatingWorkspace | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  // CLO readiness: what the indenture tests can derive for this loan from reference data, and what is missing.
  const [cloReadiness, setCloReadiness] = useState<{ ready: boolean; gapLabels: string[]; ratingSymbol?: string; ratingFactor?: number; industry?: string; obligorGroup?: string; summary?: string; readyFor?: string[]; ratingMethod?: string } | null>(null);
  // The overview's top (journey, next step, attention) is drawn once, when the reads it is built from are in;
  // until then a placeholder of its shape holds the space. Later refreshes update it in place.
  const [overviewReady, setOverviewReady] = useState(false);
  const firstReadsDoneRef = React.useRef(false);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [loanAccruals, setLoanAccruals] = useState<LoanAccrualRow[]>([]);
  const [manualEvidenceAttested, setManualEvidenceAttested] = useState(false);
  const [manualEvidenceNote, setManualEvidenceNote] = useState('');
  const [actionKind, setActionKind] = useState<CreditActionKind | null>(null);
  const [actionInitial, setActionInitial] = useState<{ amount?: string; evidenceRef?: string } | undefined>(undefined);
  const [refreshVersion, setRefreshVersion] = useState(0);
  // Ask Axiom's "prepare" chip (slice 6) and a queue item's next action open one servicing form through
  // ?action=…; the form opens once and the address is cleaned, so a reload does not reopen it.
  useEffect(() => {
    if (!isFacilityPage) return;
    const requested = searchParams.get('action');
    const kinds: Record<string, CreditActionKind> = { drawdown: 'drawdown', 'ddtl-draw': 'drawdown', paydown: 'paydown', 'rate-reset': 'rate-reset', mature: 'payoff' };
    if (!requested || !kinds[requested]) return;
    setActionKind(kinds[requested]);
    // F-PCP-044: the amount and evidence a proposal stated prefill the form; consumed once with the action.
    setActionInitial({ amount: searchParams.get('amount') || undefined, evidenceRef: searchParams.get('evidence') || undefined });
    const next = new URLSearchParams(searchParams);
    next.delete('action'); next.delete('amount'); next.delete('evidence');
    setSearchParams(next, { replace: true });
  }, [isFacilityPage, searchParams, setSearchParams]);
  // The list row is only an identity/bootstrap object. Once the persisted
  // workspace arrives, render its IBOR-backed balances and governed metadata.
  const loan = useMemo<LoanLike>(() => ({
    ...selectedLoan,
    ...(opsWorkspace?.loan || {}),
    id: selectedLoan.id,
    source: selectedLoan.source,
  }), [selectedLoan, opsWorkspace?.loan]);

  // CLO readiness is fetched once the loan is known.
  useEffect(() => {
    if (loan.source !== 'LEVERAGED_LOAN' || !loan.id) { setCloReadiness(null); return; }
    let cancelled = false;
    axios.get(`/api/credit/loans/${loan.id}/clo-readiness`)
      .then(res => { if (!cancelled) setCloReadiness(res.data); })
      .catch(() => { if (!cancelled) setCloReadiness(null); });
    return () => { cancelled = true; };
  }, [loan.source, loan.id]);

  const opsEndpoint = `/api/credit/loan-ops/${selectedLoan.source}/${selectedLoan.id}`;

  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab === 'settlement' ? 'overview' : defaultTab);
  }, [defaultTab]);

  const selectTab = (tab: DrawerTabKey) => {
    const destination = tab === 'settlement' ? 'overview' : tab;
    setActiveTab(destination);
    if (onTabChange) {
      onTabChange(destination);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('loanTab', destination);
    setSearchParams(next, { replace: true });
  };

  const loadOpsWorkspace = async () => {
    setOpsLoading(true);
    setOpsError(null);
    try {
      const response = await axios.get(`${opsEndpoint}/workspace`);
      setOpsWorkspace(response.data || null);
    } catch (e: any) {
      setOpsWorkspace(null);
      setOpsError(e?.response?.data?.message || e?.message || 'Failed to load persisted operating controls.');
    } finally {
      setOpsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadEvents = async () => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const sid = securityIdOf(loan);
        const endpoint = loan.source === 'PRIVATE_CREDIT'
          ? (sid ? `/api/private-credit/by-security/${sid}/lifecycle-events` : null)
          : `/api/credit/loans/${loan.id}/lifecycle-events`;

        if (!endpoint) {
          setEvents([]);
          setEventsError('Private credit lifecycle lookup requires a mapped security id.');
          return;
        }

        const response = await axios.get(endpoint);
        if (!cancelled) setEvents(Array.isArray(response.data) ? response.data : []);
      } catch (e: any) {
        if (!cancelled) {
          setEvents([]);
          setEventsError(e?.response?.data?.message || e?.message || 'Failed to load lifecycle events.');
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [loan, refreshVersion]);

  useEffect(() => {
    let cancelled = false;
    setAgreementJobId(null);
    if (loan.source === 'PRIVATE_CREDIT') {
      findAgreementJobForLoan(Number(loan.id))
        .then(jobId => { if (!cancelled) setAgreementJobId(jobId); })
        .catch(() => { /* the link is a convenience; the facility page works without it */ });
    }
    return () => { cancelled = true; };
  }, [loan.source, loan.id]);

  useEffect(() => {
    let cancelled = false;
    setLoanCard(undefined);
    if (loan.source !== 'LEVERAGED_LOAN') return undefined;
    axios.get(`/api/credit/loans/${loan.id}/facility-card`)
      .then(response => { if (!cancelled) setLoanCard(response.data || null); })
      .catch(() => { if (!cancelled) setLoanCard(null); });
    return () => { cancelled = true; };
  }, [loan.source, loan.id, refreshVersion]);

  const loanSecurityId = securityIdOf(loan);
  useEffect(() => {
    let cancelled = false;
    setAcquisitionTicket(undefined);
    if (loan.source !== 'PRIVATE_CREDIT') return undefined;
    if (loanSecurityId == null) { setAcquisitionTicket(null); return undefined; }
    getAcquisitionTicket(Number(loanSecurityId))
      .then(ticket => { if (!cancelled) setAcquisitionTicket(ticket); })
      .catch(() => { if (!cancelled) setAcquisitionTicket(null); });
    return () => { cancelled = true; };
  }, [loan.source, loanSecurityId, refreshVersion]);

  useEffect(() => {
    setOverviewReady(false);
    // Never hold the page longer than this, whatever is still loading.
    const fallback = window.setTimeout(() => setOverviewReady(true), 4000);
    return () => window.clearTimeout(fallback);
  }, [loan.source, loan.id]);
  useEffect(() => {
    if (firstReadsDoneRef.current) setOverviewReady(true);
  });

  useEffect(() => {
    loadOpsWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan.source, loan.id, refreshVersion]);

  useEffect(() => {
    let cancelled = false;
    if (loan.source !== 'LEVERAGED_LOAN') {
      setLoanAccruals([]);
      return () => { cancelled = true; };
    }
    axios.get(`/api/credit/loans/${loan.id}/accruals`)
      .then(response => {
        if (!cancelled) setLoanAccruals(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setLoanAccruals([]);
      });
    return () => { cancelled = true; };
  }, [loan.source, loan.id, refreshVersion]);

  const borrower = loanDisplayName(loan);
  const loanCusip = loan.security?.cusip || loan.cusip;
  const lifecyclePosition: CreditPositionLite = {
    loanRecordId: Number(loan.id),
    securityId: loan.securityId || loan.security?.id || 0,
    loanName: loan.loanName || loan.borrowerName || `Loan ${loan.id}`,
    borrowerName: loan.borrowerName,
    portfolioId: loan.portfolioId,
    cusip: loanCusip,
    // These three balances are the selected investor's official IBOR exposure.
    // Facility-global LL balances are carried separately below for borrower
    // event validation and must never replace the portfolio amounts.
    fundedAmount: loan.fundedAmount,
    currentCommitment: loan.currentCommitment,
    unfundedAmount: loan.unfundedAmount,
    spreadBps: loan.spreadBps,
    allInRate: loan.allInRate,
    baseRate: loan.baseRate,
    // F-PCP-029: the agreement's floor and CSA travel with the position so the rate-reset form starts from the record.
    floorRate: loan.floorRate,
    csaBps: loan.csaBps,
    maturityDate: loan.maturityDate,
    status: loan.status,
    facilityType: loan.facilityType,
    ddtlCommitment: loan.source === 'LEVERAGED_LOAN' ? loan.currentCapacity : loan.ddtlCommitment,
    ddtlFunded: loan.source === 'LEVERAGED_LOAN' ? loan.globalOutstanding : loan.ddtlFunded,
    ddtlUndrawn: loan.source === 'LEVERAGED_LOAN' ? loan.globalAvailability : loan.ddtlUndrawn,
    ddtlExpiry: loan.ddtlExpiry,
    globalFacilityOutstanding: loan.source === 'LEVERAGED_LOAN' ? loan.globalOutstanding : undefined,
    globalFacilityAvailability: loan.source === 'LEVERAGED_LOAN' ? loan.globalAvailability : undefined,
    drawWindowEnd: loan.ddtlExpiry,
  };
  const linkedCovenants = useMemo(() => {
    if (opsWorkspace) return opsWorkspace.covenants || [];
    return covenants.filter(c => covenantAppliesToLoan(loan, c));
  }, [loan, covenants, opsWorkspace]);
  const linkedAlerts = useMemo(
    () => covenantAlerts.filter(a =>
      matchByBorrower(loan.borrowerName, a.covenant?.borrowerName)
      || linkedCovenants.some(c => c.id != null && c.id === a.covenant?.id)
    ),
    [loan.borrowerName, covenantAlerts, linkedCovenants]
  );
  const linkedWatchlist = useMemo(
    () => watchlist.find(w => matchByBorrower(loan.borrowerName, w.borrowerName)),
    [loan.borrowerName, watchlist]
  );
  const linkedDistressed = useMemo(
    () => distressed.find(s => matchByBorrower(loan.borrowerName, s.companyName)),
    [loan.borrowerName, distressed]
  );
  const linkedTrades = useMemo(
    () => trades.filter(t => tradeAppliesToLoan(loan, t)),
    [loan, trades]
  );
  const persistedPacket = opsWorkspace?.underwritingPacket;
  const persistedDocuments = parseJsonArray(persistedPacket?.requiredDocuments);
  const persistedDocumentEvidence = documentEvidenceDoc(persistedDocuments);
  const persistedManualEvidence = persistedDocumentEvidence?.source === 'MANUAL_ATTESTATION';
  const persistedLme = opsWorkspace?.lmeAssessment;
  const persistedWorkoutCase = opsWorkspace?.workoutCase;
  const persistedExceptions = opsWorkspace?.exceptions || [];
  const latestLoanAccrual = loanAccruals[0];
  const openPersistedExceptions = persistedExceptions.filter(e => !['RESOLVED', 'WAIVED'].includes((e.queueStatus || '').toUpperCase()));
  const unqueuedLinkedAlerts = linkedAlerts.filter(alert => {
    const covenantId = alert.covenant?.id;
    const title = alert.covenant?.covenantName || 'Covenant alert';
    return !openPersistedExceptions.some(e =>
      (e.exceptionType || '').toUpperCase() === 'COVENANT_ALERT'
      && ((covenantId != null
        && e.sourceTable === 'covenant_definition'
        && String(e.sourceId) === String(covenantId))
        || e.title === title)
    );
  });
  const failedEvents = events.filter(e => (e.status || '').toUpperCase() === 'FAILED');
  useEffect(() => {
    setManualEvidenceAttested(Boolean(persistedManualEvidence && persistedDocumentEvidence?.ready));
    setManualEvidenceNote(persistedManualEvidence ? (persistedDocumentEvidence?.note || '') : '');
  }, [persistedManualEvidence, persistedDocumentEvidence?.ready, persistedDocumentEvidence?.note]);

  // The recorded lineage names the agreement for either lane; the job lookup covers private credit
  // facilities booked before that lineage existed.
  const sourceAgreementJobId: string | null = agreementJobId
    || opsWorkspace?.documentReferenceLinks?.find(link => link.jobId)?.jobId
    || loanCard?.dealPanel?.documentEvidence?.find((entry: any) => entry?.jobId)?.jobId
    || null;
  const underwritingChecksBase = buildUnderwritingChecks(
    loan, linkedCovenants, events, opsWorkspace?.documentReferenceLinks, sourceAgreementJobId,
  );
  const evidenceAttestationActive = manualEvidenceAttested && manualEvidenceNote.trim().length > 0;
  const underwritingChecks = underwritingChecksBase.map(check => {
    if (check.label !== 'Document evidence trail' || check.ok || !evidenceAttestationActive) return check;
    return {
      ...check,
      ok: true,
      detail: `Manual IC evidence attested: ${manualEvidenceNote.trim()}`,
      source: 'MANUAL_ATTESTATION',
      note: manualEvidenceNote.trim(),
    };
  });
  const underwritingReady = underwritingChecks.filter(c => c.ok).length;
  const readinessScore = Math.round((underwritingReady / underwritingChecks.length) * 100);
  // BUG-020: surface the backend-persisted LME assessment verbatim. No client-side preview
  // score is computed; until the backend has scored the asset we show a placeholder driver
  // and a "—" score so the pre-score state never diverges from the governed assessment.
  const hasPersistedLme = persistedLme != null && persistedLme.score != null;
  const lmeDrivers = hasPersistedLme ? parseJsonArray(persistedLme.drivers) : lmeNotScoredDrivers;
  const lmeScore: number | null = hasPersistedLme ? asNumber(persistedLme.score) : null;
  // Display the persisted severity band DIRECTLY; only derive from the score (with the same
  // backend cutoffs) if the persisted assessment somehow omitted severity.
  const lmeSeverity: string | null = hasPersistedLme
    ? (persistedLme.severity || lmeSeverityFromScore(asNumber(persistedLme.score)))
    : null;
  const lmeTone: Tone = lmeSeverityTone(lmeSeverity || undefined);
  const openExceptionCount = openPersistedExceptions.length + unqueuedLinkedAlerts.length + linkedTrades.length + failedEvents.length + (linkedWatchlist ? 1 : 0);
  // Signals are everything in that count that nobody has recorded as an exception yet.
  const signalCount = openExceptionCount - openPersistedExceptions.length;


  const openWorkoutModule = () => onNavigate('distressed', {
    loanSource: loan.source,
    loanId: loan.id,
    workoutCaseId: persistedWorkoutCase?.id,
    distressedSituationId: persistedWorkoutCase?.distressedSituationId || linkedDistressed?.id,
    borrower,
  });

  const covenantActual = (c: CovenantRow) => c.latestActualValue ?? c.actualValue;
  const covenantCushion = (c: CovenantRow) => c.latestCushionPct ?? c.cushionPct;
  // F-REG-022 family: backend cushion_pct mixes conventions (fraction-of-limit
  // for MAX covenants, multiple-of-threshold for MIN). When threshold and actual
  // are both present, compute the honest headroom from them; the stored value is
  // only a last resort.
  const covenantCushionText = (c: CovenantRow) => {
    const thresholdType = (c.thresholdType || '').toUpperCase();
    // A test is measured against the threshold in force for its period. After an amendment that
    // is not today's threshold, so the test's own threshold comes first (F-PAR-020).
    const tested = c.latestThresholdValue != null ? Number(c.latestThresholdValue) : NaN;
    const threshold = Number.isFinite(tested) ? tested : Number(c.thresholdValue);
    const actual = Number(covenantActual(c));
    if (Number.isFinite(threshold) && threshold !== 0 && Number.isFinite(actual)) {
      const amended = Number.isFinite(tested) && c.thresholdValue != null && tested !== Number(c.thresholdValue)
        ? ` · tested at ${fmtCovenantValue(tested)}` : '';
      if (thresholdType === 'MAX') return `${(((threshold - actual) / threshold) * 100).toFixed(1)}% below limit${amended}`;
      if (thresholdType === 'MIN') {
        const coverage = (actual / threshold) * 100;
        return `${coverage >= 200 ? `${(coverage / 100).toFixed(1)}x floor` : `${(coverage - 100).toFixed(1)}% above floor`}${amended}`;
      }
    }
    const v = covenantCushion(c);
    return v == null ? '--' : percent(Math.abs(v) <= 1 ? v * 100 : v);
  };
  // #76: metric-aware covenant value formatting. Ratios (leverage, coverage,
  // DSCR/FCCR) are small multiples → "6.80x"; dollar covenants (CapEx,
  // Liquidity, EBITDA) are large amounts → "$25.0M". numberCompact rounded a
  // 6.80 ratio to "7" and printed thresholds raw (6.5, 25000000).
  const fmtCovenantValue = (v?: number | null) => {
    if (v == null || Number.isNaN(Number(v))) return '--';
    const n = Number(v);
    const abs = Math.abs(n);
    if (abs >= 1000) {
      if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
      if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
      return `$${(n / 1e3).toFixed(0)}K`;
    }
    return `${n.toFixed(2)}x`;
  };
  const covenantActualText = (c: CovenantRow) => fmtCovenantValue(covenantActual(c));
  const covenantThresholdText = (c: CovenantRow) =>
    c.thresholdValueMax != null
      ? `${fmtCovenantValue(c.thresholdValue)} – ${fmtCovenantValue(c.thresholdValueMax)}`
      : fmtCovenantValue(c.thresholdValue);
  const covenantCureText = (c: CovenantRow) => {
    const general = c.curePeriodDays != null ? `${c.curePeriodDays}-day general cure` : 'No general cure stated';
    const equity = c.hasEquityCure
      ? (c.maxEquityCures == null ? 'Equity cure permitted' : `${c.maxEquityCures} equity cures`)
      : 'No equity cure';
    return `${general} · ${equity}`;
  };

  const tabs: Array<{ id: DrawerTabKey; label: string; icon: React.ReactNode; count?: number | string }> = [
    { id: 'overview', label: loan.source === 'LEVERAGED_LOAN' ? 'Facility & Exposure' : 'Overview', icon: <BookOpen /> },
    ...(loan.source === 'PRIVATE_CREDIT' && presentation === 'page' ? [{ id: 'agreement' as DrawerTabKey, label: 'Agreement', icon: <FileText /> }, { id: 'servicing' as DrawerTabKey, label: 'Servicing', icon: <RefreshCw /> }] : []),
    { id: 'underwriting', label: 'Underwriting', icon: <FileSearch />, count: underwritingChecks.length - underwritingReady },
    { id: 'covenants', label: 'Covenants', icon: <ClipboardCheck />, count: linkedCovenants.length },
    { id: 'lme', label: 'LME Risk', icon: <ShieldAlert />, count: lmeScore != null ? `${lmeScore} / 100` : undefined },
    { id: 'workout', label: 'Workout', icon: <Gavel />, count: linkedDistressed ? 1 : linkedWatchlist ? 1 : undefined },
    { id: 'exceptions', label: signalCount > 0 ? `Exceptions · ${signalCount} signal${signalCount === 1 ? '' : 's'}` : 'Exceptions', icon: <ListChecks />, count: openPersistedExceptions.length },

    ...(loan.source === 'PRIVATE_CREDIT' && presentation === 'page' ? [{ id: 'documents' as DrawerTabKey, label: 'Documents', icon: <FileSearch /> }, { id: 'activity' as DrawerTabKey, label: 'Activity', icon: <BookOpen /> }] : []),
  ];

  const walkCommitment = asNumber(loan.currentCommitment ?? loan.originalCommitment);
  const walkFunded = asNumber(loan.fundedAmount);
  const walkUnfunded = asNumber(loan.unfundedAmount ?? loan.ddtlUndrawn) || Math.max(0, walkCommitment - walkFunded);
  const walkBasis = Math.max(walkCommitment, walkFunded + walkUnfunded, 1);
  const walkPct = (value: number) => (value / walkBasis) * 100;
  const utilizationPct = walkCommitment > 0 ? (walkFunded / walkCommitment) * 100 : null;

  const renderMetrics = () => (
    <MetricStrip>
      <MetricCard $tone="blue">
        <MetricLabel>{loan.currentFacilityCapacity != null ? 'Contractual Capacity' : 'Funded'} <ExplainMetricButton scope="current" metric={loan.currentFacilityCapacity != null ? 'CAPACITY' : 'FUNDED'} /></MetricLabel>
        <MetricValue>{moneyCompact(loan.currentFacilityCapacity ?? loan.fundedAmount)}</MetricValue>
        <MetricSubtext>{loan.currentFacilityCapacity != null ? 'Borrower facility; not portfolio exposure' : `${moneyFull(loan.currentCommitment ?? loan.originalCommitment)} commitment`}</MetricSubtext>
      </MetricCard>
      <MetricCard $tone="cyan">
        <MetricLabel>{loan.currentFacilityCapacity != null ? 'Official Funded Exposure' : 'Unfunded'} <ExplainMetricButton scope="current" metric={loan.currentFacilityCapacity != null ? 'FUNDED' : 'UNFUNDED'} /></MetricLabel>
        <MetricValue>{moneyCompact(loan.currentFacilityCapacity != null ? loan.fundedAmount : loan.unfundedAmount ?? loan.ddtlUndrawn)}</MetricValue>
        <MetricSubtext>{loan.currentFacilityCapacity != null ? 'From current IBOR positions only' : plainCode(loan.facilityType) || sourceLabel(loan.source)}</MetricSubtext>
      </MetricCard>
      <MetricCard $tone="violet">
        <MetricLabel>All-in Rate <ExplainMetricButton scope="current" metric="ALL_IN_RATE" /></MetricLabel>
        <MetricValue>{loan.allInRate != null ? percent(loan.allInRate) : 'Not recorded'}</MetricValue>
        <MetricSubtext>{loan.spreadBps != null ? `${loan.spreadBps} bps spread` : 'Spread not recorded'}</MetricSubtext>
      </MetricCard>
      <MetricCard $tone={statusTone(loan.status)}>
        <MetricLabel>Status</MetricLabel>
        <MetricValue>{plainCode(loan.status) || '--'}</MetricValue>
        <MetricSubtext>Maturity {fmtDate(loan.maturityDate)}</MetricSubtext>
      </MetricCard>
      <MetricCard
        $tone={opsLoading || opsError || eventsLoading || eventsError ? 'blue' : openExceptionCount === 0 ? 'emerald' : 'amber'}
        style={{ cursor: 'pointer' }}
        onClick={() => selectTab('exceptions')}
      >
        <MetricLabel>Attention <ExplainMetricButton scope="current" metric="ATTENTION" /></MetricLabel>
        <MetricValue>{opsError || eventsError ? 'Unavailable' : opsLoading || eventsLoading ? 'Loading' : openExceptionCount === 0 ? 'Clear'
          : openPersistedExceptions.length > 0 ? openPersistedExceptions.length : signalCount}</MetricValue>
        <MetricSubtext>{opsError || eventsError ? 'Exceptions and signals could not be loaded'
          : opsLoading || eventsLoading ? 'Reading exceptions and signals'
          : openExceptionCount === 0 ? 'No open exceptions or signals'
          : openPersistedExceptions.length > 0
            ? `open exception${openPersistedExceptions.length === 1 ? '' : 's'}${signalCount > 0 ? ` · ${signalCount} signal${signalCount === 1 ? '' : 's'}` : ''}`
            : `signal${signalCount === 1 ? '' : 's'} awaiting review · no exceptions`}</MetricSubtext>
      </MetricCard>
    </MetricStrip>
  );

  // Recorded exceptions and unqueued signals are different things and are counted apart:
  // the Exceptions tab lists them in separate sections (F-PAR-010, F-PAR-025).
  const signalsBreakdown = [
    linkedTrades.length && `${linkedTrades.length} open trade ticket${linkedTrades.length === 1 ? '' : 's'}`,
    unqueuedLinkedAlerts.length && `${unqueuedLinkedAlerts.length} covenant alert${unqueuedLinkedAlerts.length === 1 ? '' : 's'}`,
    failedEvents.length && `${failedEvents.length} failed lifecycle event${failedEvents.length === 1 ? '' : 's'}`,
    linkedWatchlist && '1 watchlist entry',
  ].filter(Boolean).join(' · ');
  const loanAttention = { exceptions: openPersistedExceptions.length, signals: signalCount, signalsDetail: signalsBreakdown };
  const attentionBreakdown = [
    openPersistedExceptions.length && `${openPersistedExceptions.length} exception${openPersistedExceptions.length === 1 ? '' : 's'}`,
    signalsBreakdown,
  ].filter(Boolean).join(' · ');
  const packetDocuments = parseJsonArray(persistedPacket?.requiredDocuments);
  const facilityJourney = loan.source === 'PRIVATE_CREDIT' ? privateCreditJourney({
    ticket: acquisitionTicket && { id: acquisitionTicket.id, status: acquisitionTicket.status, hasPortfolio: acquisitionTicket.portfolioId != null },
    fromAgreement: Boolean(sourceAgreementJobId),
    commitment: moneyFull(loan.currentCommitment ?? loan.originalCommitment),
    funded: walkFunded,
    fundedText: moneyFull(loan.fundedAmount),
    packetStatus: persistedPacket?.packetStatus || null,
    firstDrawNeedsApprovedPacket: ['REQUIRED', 'REQUIRED_FOR_INITIAL_DRAW'].includes(tenantConfig?.pcFundingControlPolicy || ''),
    packetReadiness: persistedPacket?.readinessScore != null ? Number(persistedPacket.readinessScore) : null,
    packetDocumentsNotReady: packetDocuments.filter(d => d && d.ready !== true && d.ready !== 'true').map(d => String(d.label || 'A required document')),
    attention: loanAttention,
  }) : leveragedLoanJourney({
    loaded: loanCard !== undefined,
    dealName: loanCard?.dealPanel?.dealName || loanCard?.dealPanel?.dealCode || null,
    fromAgreement: Boolean(sourceAgreementJobId),
    ticket: linkedTrades[0]?.id != null
      ? { id: linkedTrades[0].id, status: linkedTrades[0].status || '', hasPortfolio: Boolean(linkedTrades[0].portfolioName) }
      : null,
    pending: (loanCard?.portfolioPanel?.pendingTrades || []).map((trade: any) => ({
      tradeId: trade.tradeId, side: trade.side, parText: moneyFull(trade.par),
      expectedSettleDate: trade.expectedSettleDate ? fmtDate(trade.expectedSettleDate) : null,
      daysLate: daysBetween(trade.expectedSettleDate, businessDate),
      settlementStatus: trade.settlementStatus || null, legalTransferStatus: trade.legalTransferStatus || null,
      exceptionReason: trade.settlementExceptionReason || null,
    })),
    holdingsExist: Boolean(loanCard?.portfolioPanel?.holdingsExist),
    settledText: moneyFull(loanCard?.portfolioPanel?.aggregate?.settledExposure),
    portfolioCount: (loanCard?.portfolioPanel?.portfolios || []).length,
    attention: loanAttention,
  });
  const followNextStep = (target: NextStepTarget) => {
    if (target === 'TICKET') navigate(loanCusip ? blotterLoanPath(loanCusip) : '/bond-transactions');
    else if (target === 'SETTLEMENT') navigate('/middle-office?tab=loan-settlement');
    else if (target === 'CREATE_TICKET') navigate(loan.source === 'LEVERAGED_LOAN' ? leveragedLoanTicketPath(loanCusip) : privateCreditTicketPath(loanCusip), leveragedLoanTicketState({
      securityId: securityIdOf(loan), cusip: loanCusip, securityName: loan.loanName || borrower, loanId: loan.id,
      loanName: loan.loanName, borrowerName: loan.borrowerName, facilityType: loan.facilityType,
    }));
    else if (target === 'UNDERWRITING') selectTab('underwriting');
    else if (target === 'DRAWDOWN') setActionKind('drawdown');
    else if (target === 'EXCEPTIONS' || target === 'SIGNALS') {
      // Land on the section that holds the work, not always on the recorded worklist.
      setExceptionSection(target === 'SIGNALS' ? 'signals' : 'queue');
      selectTab('exceptions');
    }
  };

  // The private credit facility page on the ABF standard (PC workflow parity, slice 2): the attention
  // rail's items name the tab or record that owns their action; this maps each onto the page's own
  // controls. Nothing here changes a record.
  const pcFacilityPage = isFacilityPage && loan.source === 'PRIVATE_CREDIT';
  // F-PCP-012: the attention service's count for this facility (same source as the board, rail and queue).
  const [pcAttention, setPcAttention] = useState<{ open: number; critical: number; workflows: string } | null>(null);
  useEffect(() => {
    if (!pcFacilityPage || !loan?.id) { setPcAttention(null); return; }
    let alive = true;
    getPcAttention(Number(loan.id)).then(items => {
      if (!alive) return;
      const open = items.filter(i => i.triageState === 'OPEN');
      const byWorkflow = open.reduce<Record<string, number>>((acc, i) => { acc[i.workflow] = (acc[i.workflow] || 0) + 1; return acc; }, {});
      setPcAttention({ open: open.length, critical: open.filter(i => i.severity === 'CRITICAL').length,
        workflows: Object.entries(byWorkflow).map(([w, n]) => `${n} ${w.toLowerCase().replace(/_/g, ' ')}`).join(' · ') || 'none' });
    }).catch(() => { if (alive) setPcAttention(null); });
    return () => { alive = false; };
  }, [pcFacilityPage, loan?.id, refreshVersion]);

  const openFromAttention = (workspace: string, query?: string | null) => {
    const action = new URLSearchParams(query || '').get('action');
    if (action === 'mature') { setActionKind('payoff'); return; }
    if (action === 'ddtl-draw') { setActionKind('drawdown'); return; }
    if (action === 'rate-reset') { setActionKind('rate-reset'); return; }
    if (workspace === 'ticket') { navigate(loanCusip ? blotterLoanPath(loanCusip) : '/bond-transactions'); return; }
    if (workspace === 'documents') { navigate(sourceAgreementJobId ? agreementReviewPath(sourceAgreementJobId) : '/middle-office?tab=document-processing'); return; }
    if (workspace === 'exceptions') { setExceptionSection('queue'); selectTab('exceptions'); return; }
    if (['overview', 'underwriting', 'covenants', 'lme', 'workout'].includes(workspace)) { selectTab(workspace as DrawerTabKey); return; }
    selectTab('overview');
  };

  const renderOverview = () => {
    // CLO readiness is reference-data state, not an exception: it shows whether or not anything needs attention (F-CB-011).
    const cloStrip = <>
      {cloReadiness && (
        <QueueShell style={{ marginTop: 0, marginBottom: '0.8rem', cursor: 'default' }} title="What the CLO indenture tests can read for this loan">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {cloReadiness.ready ? <CheckCircle2 style={{ width: 16, height: 16, color: '#34d399', flex: 'none' }} /> : <AlertTriangle style={{ width: 16, height: 16, color: '#fcd34d', flex: 'none' }} />}
            <span style={{ color: '#f8fafc', fontWeight: 850, fontSize: '0.84rem' }}>
              {cloReadiness.ready
                ? (cloReadiness.readyFor && cloReadiness.readyFor.length > 0 ? 'Reference data complete for the eligible CLO rulebooks' : 'CLO reference data complete')
                : `CLO readiness: ${cloReadiness.gapLabels.length} ${cloReadiness.gapLabels.length === 1 ? 'gap' : 'gaps'}`}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.74rem' }}>
              {cloReadiness.summary
                ? `${cloReadiness.summary.replace(/ready for /g, 'eligible rulebooks: ')}${cloReadiness.ratingSymbol ? ` · ${cloReadiness.ratingSymbol} (factor ${cloReadiness.ratingFactor}, ${cloReadiness.ratingMethod})` : ''}${cloReadiness.industry ? ` · ${cloReadiness.industry}` : ''} — a gap is fixed in the loan reference, not per deal`
                : cloReadiness.ready
                  ? `${cloReadiness.ratingSymbol ? `${cloReadiness.ratingSymbol} (factor ${cloReadiness.ratingFactor})` : ''}${cloReadiness.industry ? ` · ${cloReadiness.industry}` : ''} — obligor, industry, rating, margin, index and schedule all derived from reference data`
                  : `${cloReadiness.gapLabels.join('; ')} — fix in the loan reference, not per deal`}
            </span>
          </div>
        </QueueShell>
      )}
    </>;
    const attention = <>
      {openExceptionCount > 0 ? (
        <QueueShell style={{ cursor: 'pointer', marginTop: 0, marginBottom: '0.8rem' }} onClick={() => setActiveTab('exceptions')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <AlertTriangle style={{ width: 16, height: 16, color: '#fcd34d', flex: 'none' }} />
            <span style={{ color: '#f8fafc', fontWeight: 850, fontSize: '0.84rem' }}>
              {openExceptionCount === 1 ? '1 item needs attention' : `${openExceptionCount} items need attention`}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.74rem' }}>
              {attentionBreakdown} — open the exception queue
            </span>
          </div>
        </QueueShell>
      ) : (
        <AllClearShell style={{ marginTop: 0, marginBottom: '0.8rem' }}>
          <CheckCircle2 /> No open attention items in the loaded loan records.
        </AllClearShell>
      )}

    </>;
    const arrangement = <>
      {loan.source === 'PRIVATE_CREDIT' && loan.arrangement && (
        <WorkspacePanel>
          <PanelHeader>
            <SectionHeader>
              <Landmark />
              <div>
                <PanelTitle>{loan.arrangement.arrangementName || 'Financing Arrangement'}</PanelTitle>
                <PanelSubtitle>
                  {loan.arrangement.arrangementCode || `Arrangement #${loan.arrangement.id}`} · legal/reference parent · non-investable
                </PanelSubtitle>
              </div>
            </SectionHeader>
            <StatusPill $tone={statusTone(loan.arrangement.arrangementStatus)}>{loan.arrangement.arrangementStatus || 'EXPECTED'}</StatusPill>
          </PanelHeader>
          <PanelBody>
            <DefColumns style={{ marginTop: 0 }}>
              <DefGroup>
                <DefGroupTitle>Selected Facility</DefGroupTitle>
                <DefRow><DefLabel>Facility code</DefLabel><DefValue>{loan.facilityCode || '--'}</DefValue></DefRow>
                <DefRow><DefLabel>Contractual capacity</DefLabel><DefValue>{moneyFull(loan.currentFacilityCapacity)}</DefValue></DefRow>
                <DefRow><DefLabel>Capacity evidence</DefLabel><DefValue>{loan.capacitySource || 'Not evidenced'}{loan.capacityAsOf ? ` · ${fmtDate(loan.capacityAsOf)}` : ''}</DefValue></DefRow>
                <DefRow><DefLabel>Facility state</DefLabel><DefValue>{loan.facilityState || '--'}</DefValue></DefRow>
              </DefGroup>
              <DefGroup>
                <DefGroupTitle>Arrangement Context</DefGroupTitle>
                <DefRow><DefLabel>Agreement date</DefLabel><DefValue>{fmtDate(loan.arrangement.agreementDate)}</DefValue></DefRow>
                <DefRow><DefLabel>Governing law</DefLabel><DefValue>{loan.arrangement.governingLaw || '--'}</DefValue></DefRow>
                <DefRow><DefLabel>Facilities</DefLabel><DefValue>{loan.arrangement.facilities?.length || 0}</DefValue></DefRow>
                <DefRow><DefLabel>Exposure authority</DefLabel><DefValue>Facility trades and official IBOR positions</DefValue></DefRow>
              </DefGroup>
            </DefColumns>
            {(loan.arrangement.facilities?.length || 0) > 1 && (
              <TableShell style={{ marginTop: 12 }}>
                <DataTable style={{ minWidth: 720 }}>
                  <thead><tr><th>Facility</th><th>Type</th><th>State</th><th>Contractual Capacity</th><th>Maturity</th></tr></thead>
                  <tbody>{loan.arrangement.facilities?.map(facility => (
                    <tr key={facility.id}>
                      <td><RowTitle>{facility.facilityCode || facility.loanName || '--'}</RowTitle></td>
                      <td>{String(facility.facilityType || '--').replace(/_/g, ' ')}</td>
                      <td><StatusPill $tone={statusTone(facility.facilityState)}>{facility.facilityState || '--'}</StatusPill></td>
                      <td>{facility.commitmentCurrency || ''} {numberCompact(facility.currentFacilityCapacity)}</td>
                      <td>{fmtDate(facility.maturityDate)}</td>
                    </tr>
                  ))}</tbody>
                </DataTable>
              </TableShell>
            )}
            {(loan.arrangement.relationships?.length || 0) > 0 && (
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {loan.arrangement.relationships?.map(relationship => (
                  <EvidenceChip key={relationship.id} $tone={relationship.relationshipScope === 'LENDER_ECONOMIC' ? 'amber' : 'cyan'}>
                    {relationship.fromFacilityCode} {String(relationship.relationshipType || '').replace(/_/g, ' ').toLowerCase()} {relationship.toFacilityCode}
                  </EvidenceChip>
                ))}
              </div>
            )}
          </PanelBody>
        </WorkspacePanel>
      )}

    </>;
    const utilization = <>
      {walkCommitment > 0 && (
        <WalkShell>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
            <div style={{ color: '#f8fafc', fontSize: '0.9rem', fontWeight: 850 }}>Commitment Utilization</div>
            {utilizationPct != null && (
              <EvidenceChip $tone={utilizationPct >= 90 ? 'amber' : 'cyan'}>
                {utilizationPct.toFixed(1)}% utilized
              </EvidenceChip>
            )}
          </div>
          <WalkRow>
            <WalkLabel>Total commitment</WalkLabel>
            <WalkTrack><WalkFill $pct={walkPct(walkCommitment)} $tone="slate" /></WalkTrack>
            <WalkValue>{moneyFull(walkCommitment)}</WalkValue>
          </WalkRow>
          <WalkRow>
            <WalkLabel>- funded / drawn</WalkLabel>
            <WalkTrack><WalkFill $pct={walkPct(walkFunded)} $tone="amber" /></WalkTrack>
            <WalkValue $tone="deduct">({moneyFull(walkFunded)})</WalkValue>
          </WalkRow>
          <WalkRow>
            <WalkLabel $emphasis>= Unfunded available</WalkLabel>
            <WalkTrack><WalkFill $pct={walkPct(walkUnfunded)} $tone="emerald" /></WalkTrack>
            <WalkValue $tone="result">{moneyFull(walkUnfunded)}</WalkValue>
          </WalkRow>
        </WalkShell>
      )}

    </>;
    const lifecycleActions = (<ActionBar>
            {CORE_CREDIT_LIFECYCLE_ACTIONS.map(action => (
              <CreditLifecycleActionButton
                key={action.kind}
                showLabel
                action={action}
                onClick={() => setActionKind(action.kind)}
              />
            ))}
          </ActionBar>);
    const borrower = <>
      <WorkspacePanel>
        <PanelHeader>
          <SectionHeader>
            <Building2 aria-hidden="true" />
            <div>
              <PanelTitle>{isFacilityPage ? 'Borrower & Facility' : 'Borrower / Loan 360'}</PanelTitle>
              <PanelSubtitle>Single-name context for front office, surveillance, and middle-office workflows.</PanelSubtitle>
            </div>
          </SectionHeader>
        </PanelHeader>
        <PanelBody>
          <DefColumns style={{ marginTop: 0 }}>
            <DefGroup>
              <DefGroupTitle>Borrower</DefGroupTitle>
              <DefRow><DefLabel>Borrower</DefLabel><DefValue>{loan.borrowerName || '--'}</DefValue></DefRow>
              <DefRow><DefLabel>Sponsor</DefLabel><DefValue>{loan.sponsorName || 'Not mapped'}</DefValue></DefRow>
              <DefRow><DefLabel>Sector</DefLabel><DefValue>{loan.sector || '--'}</DefValue></DefRow>
              <DefRow><DefLabel>Ratings</DefLabel><DefValue>S&amp;P {loan.spRating || '--'} · Moody's {loan.moodysRating || '--'} · Fitch {loan.fitchRating || '--'}</DefValue></DefRow>
            </DefGroup>
            <DefGroup>
              <DefGroupTitle>Facility</DefGroupTitle>
              <DefRow><DefLabel>Facility</DefLabel><DefValue>{plainCode(loan.facilityType) || '--'}</DefValue></DefRow>
              <DefRow><DefLabel>Seniority</DefLabel><DefValue>{plainCode(loan.seniority) || '--'}</DefValue></DefRow>
              <DefRow><DefLabel>PIK</DefLabel><DefValue>{loan.pikElectionCurrent || loan.pikToggle || 'Cash pay / not mapped'}</DefValue></DefRow>
              <DefRow><DefLabel>Identifier</DefLabel><DefValue>{loan.loanId || loan.security?.cusip || loan.security?.isin || '--'}</DefValue></DefRow>
            </DefGroup>
          </DefColumns>
        </PanelBody>
      </WorkspacePanel>

    </>;
    const accruals = <>
      {loan.source === 'LEVERAGED_LOAN' && ['DELAYED_DRAW', 'REVOLVER'].includes((loan.facilityType || '').toUpperCase()) && (
        <WorkspacePanel>
          <PanelHeader>
            <SectionHeader>
              <BookOpen />
              <div>
                <PanelTitle>Accruals &amp; Unfunded Fees</PanelTitle>
                <PanelSubtitle>Daily governed accruals sized from settled IBOR funded and holder-level unfunded exposure.</PanelSubtitle>
              </div>
            </SectionHeader>
          </PanelHeader>
          <PanelBody>
            <DefColumns style={{ marginTop: 0 }}>
              <DefGroup>
                <DefGroupTitle>Fee Setup</DefGroupTitle>
                <DefRow><DefLabel>{(loan.facilityType || '').toUpperCase() === 'DELAYED_DRAW' ? 'Ticking fee' : 'Commitment fee'}</DefLabel><DefValue>{((loan.facilityType || '').toUpperCase() === 'DELAYED_DRAW' ? loan.tickingFeeBps : loan.commitmentFeeBps) != null ? `${(loan.facilityType || '').toUpperCase() === 'DELAYED_DRAW' ? loan.tickingFeeBps : loan.commitmentFeeBps} bps` : 'Not configured'}</DefValue></DefRow>
                <DefRow><DefLabel>Latest accrual date</DefLabel><DefValue>{latestLoanAccrual?.accrualDate || 'No accrual posted'}</DefValue></DefRow>
                <DefRow><DefLabel>Total daily accrual</DefLabel><DefValue>{latestLoanAccrual ? moneyFull(latestLoanAccrual.totalAccrual) : '--'}</DefValue></DefRow>
              </DefGroup>
              <DefGroup>
                <DefGroupTitle>Latest Accrual Breakdown</DefGroupTitle>
                <DefRow><DefLabel>Cash interest</DefLabel><DefValue>{latestLoanAccrual ? moneyFull(latestLoanAccrual.cashInterestAccrual) : '--'}</DefValue></DefRow>
                <DefRow><DefLabel>Unfunded fee accrual</DefLabel><DefValue>{latestLoanAccrual ? moneyFull((latestLoanAccrual.tickingFeeAccrual || 0) + (latestLoanAccrual.commitmentFeeAccrual || 0)) : '--'}</DefValue></DefRow>
                <DefRow><DefLabel>OID / PIK</DefLabel><DefValue>{latestLoanAccrual ? `${moneyFull(latestLoanAccrual.oidAmortization)} / ${moneyFull(latestLoanAccrual.pikAccrual)}` : '--'}</DefValue></DefRow>
              </DefGroup>
            </DefColumns>
          </PanelBody>
        </WorkspacePanel>
      )}
    </>;
    return <LoanOverviewWorkspace
      attention={<>{cloStrip}{pcFacilityPage ? null : opsLoading || opsError || eventsLoading || eventsError
        ? <p>{opsError || eventsError ? 'Attention status unavailable. Review the loading message above.' : 'Checking loan activity and operating controls…'}</p>
        : openExceptionCount === 0 || ['EXCEPTIONS', 'SIGNALS'].includes(facilityJourney.next.target) ? null : attention}</>}
      actions={lifecycleActions}
      followUp={<>{!['EXCEPTIONS', 'SIGNALS'].includes(facilityJourney.next.target)
          && <WorkspaceButton onClick={() => selectTab('exceptions')}>Review exceptions</WorkspaceButton>}
        <WorkspaceButton onClick={() => selectTab('underwriting')}>Review underwriting</WorkspaceButton></>}
      arrangement={loan.arrangement ? arrangement : undefined}>
      {!isFacilityPage && renderMetrics()}
      {loan.source === 'LEVERAGED_LOAN' && <LoanFacilityCardDrawer key={`${loan.id}:${refreshVersion}`} embedded
        view="facility" loanId={Number(loan.id)} loanName={loan.loanName} securityId={loan.securityId || loan.security?.id} onClose={() => {}} />}
      {loan.source === 'PRIVATE_CREDIT' && utilization}
      {loan.source === 'PRIVATE_CREDIT' && borrower}{accruals}
      {loan.source === 'LEVERAGED_LOAN' && <details><summary>Ratings & borrower details</summary>{borrower}</details>}
      <section aria-label="Recent loan activity"><h2>Recent activity</h2>
        {eventsLoading ? <p>Loading activity…</p> : eventsError ? <p>Activity unavailable: {eventsError}</p>
          : events.length === 0 ? <p>No recorded loan activity is available.</p>
            : [...events].sort((a, b) => (b.createdAt || b.eventDate || '').localeCompare(a.createdAt || a.eventDate || '')).slice(0, 5).map((event, index) =>
              <div key={event.id ?? index} style={{ padding: '14px 0', borderBottom: '1px solid #1e293b' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <strong>{(event.eventType || 'Loan event').replace(/_/g, ' ')}</strong><span>{event.status || 'Status not recorded'}</span>
                </div><p>{fmtDate(event.eventDate || event.createdAt)}{event.sourceDocumentName ? ` · ${event.sourceDocumentName}` : ''}</p>
                {event.message && <p>{event.message}</p>}
              </div>)}
      </section>
    </LoanOverviewWorkspace>;
  };


  const header = (
        <DrawerHeader>
          <div style={{ minWidth: 0 }}>
            {isFacilityPage && (
              <WorkspaceButton
                onClick={() => navigate(loanListPath(loan.source))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', padding: 0, background: 'transparent', border: 0 }}
              >
                <ArrowLeft size={15} />
                {loan.source === 'PRIVATE_CREDIT' ? 'Back to Private Credit' : 'Back to Leveraged Loans'}
              </WorkspaceButton>
            )}
            {!isFacilityPage && <div style={{ color: '#38bdf8', fontSize: 11, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.16em' }}>Borrower / Loan 360</div>}
            {isFacilityPage ? (
              <h1 style={{ margin: '0.4rem 0 0', color: '#f8fafc', fontSize: 'clamp(1.35rem, 2vw, 1.8rem)', lineHeight: 1.2 }}>{loan.loanName || borrower}</h1>
            ) : (
              <h2 style={{ margin: '0.25rem 0 0', color: '#f8fafc', fontSize: '1.35rem' }}>{borrower}</h2>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#94a3b8' }}>{sourceLabel(loan.source)}</span>
              {isFacilityPage && loan.loanName && <span style={{ color: '#94a3b8' }}>{borrower}</span>}
              <span style={{ color: '#64748b', fontSize: 12 }}>{plainCode(loan.facilityType) || 'facility pending'} · {loan.loanId || 'identifier pending'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginLeft: 'auto', flexShrink: 0 }}>
          {sourceAgreementJobId && (
            <WorkspaceButton
              title="Every term of the agreement beside the passage it was read from"
              onClick={() => navigate(agreementReviewPath(sourceAgreementJobId))}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <FileSearch size={15} />
              Agreement & sources
            </WorkspaceButton>
          )}
          <details className="loan-record-actions"><summary>Records & actions</summary><ModalActions>
            {!isFacilityPage && <WorkspaceButton onClick={() => onNavigate('loans')}>Loan Book</WorkspaceButton>}
            <WorkspaceButton
              title="Open governed underwriting and amendment workflow"
              onClick={() => selectTab('underwriting')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <FileText size={15} />
              Governed Edit
            </WorkspaceButton>
            <WorkspaceButton
              title="Open governed loan reference inventory"
              onClick={() => navigate(creditLoanReferencePath(loanCusip || loan.loanId || borrower))}
            >
              Reference
            </WorkspaceButton>
            {loanCusip && (
              <WorkspaceButton
                title="Open this loan's trade lineage in the Blotter"
                onClick={() => navigate(blotterLoanPath(loanCusip))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <List size={15} />
                Blotter
              </WorkspaceButton>
            )}
            {loanCusip && (
              <WorkspaceButton
                title="Open Security Master"
                onClick={() => navigate(securityMasterPath(loanCusip))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <Database size={15} />
                Security Master
              </WorkspaceButton>
            )}
            {(loanCusip || securityIdOf(loan)) && (
              <WorkspaceButton
                title="Create acquisition ticket"
                onClick={() => navigate(
                  loan.source === 'LEVERAGED_LOAN'
                    ? leveragedLoanTicketPath(loanCusip)
                    : privateCreditTicketPath(loanCusip),
                  leveragedLoanTicketState({
                    securityId: securityIdOf(loan),
                    cusip: loanCusip,
                    securityName: loan.loanName || borrower,
                    loanId: loan.id,
                    loanName: loan.loanName,
                    borrowerName: loan.borrowerName,
                    facilityType: loan.facilityType,
                  }),
                )}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <Ticket size={15} />
                Acquisition Ticket
              </WorkspaceButton>
            )}
            {loanCusip && (
              <WorkspaceButton
                title="Open this loan's portfolio exposure in IBOR"
                onClick={() => navigate(iborPositionPath(loanCusip))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <Briefcase size={15} />
                IBOR Exposure
              </WorkspaceButton>
            )}
            {!isFacilityPage && <WorkspaceButton onClick={onClose}>Close</WorkspaceButton>}
          </ModalActions></details>
          </div>
        </DrawerHeader>
  );

  const timelineWarning = (
    <>
        {eventsError && (
          <Driver $tone="amber" style={{ marginBottom: '1rem' }}>
            <DriverHead>
              <div>
                <DriverTitle>Lifecycle timeline unavailable</DriverTitle>
                <DriverText>{eventsError}</DriverText>
              </div>
              <FileWarning size={18} />
            </DriverHead>
          </Driver>
        )}
    </>
  );

  const navigation = (
        <DrawerTabs>
          {tabs.map(tab => (
            <DrawerTab
              key={tab.id}
              $active={activeTab === tab.id}
              aria-current={isFacilityPage && activeTab === tab.id ? 'page' : undefined}
              onClick={() => selectTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && <StatusPill $tone={tab.id === 'lme' ? lmeTone : Number(tab.count) > 0 || (tab.id === 'exceptions' && signalCount > 0) ? 'amber' : 'emerald'}>{tab.count}</StatusPill>}
            </DrawerTab>
          ))}
        </DrawerTabs>
  );

  const firstReadsDone = !opsLoading && !eventsLoading
    && (loan.source === 'PRIVATE_CREDIT' ? acquisitionTicket !== undefined : loanCard !== undefined);
  firstReadsDoneRef.current = firstReadsDone;
  const hiddenUntilReady: React.CSSProperties | undefined = overviewReady ? undefined
    : { visibility: 'hidden', height: 0, overflow: 'hidden' };

  const content = (
    <>
        {opsError && (
          <Driver $tone="red" style={{ marginBottom: '1rem' }}>
            <DriverHead>
              <div>
                <DriverTitle>{opsError ? 'Operating controls unavailable' : 'Loading operating controls'}</DriverTitle>
                <DriverText>{opsError || 'Persisted underwriting, LME, workout, covenant, and exception controls are syncing.'}</DriverText>
              </div>
              <StatusPill $tone={opsError ? 'red' : 'cyan'}>{opsError ? 'CHECK' : 'SYNC'}</StatusPill>
            </DriverHead>
          </Driver>
        )}

        {activeTab === 'overview' && !overviewReady && (
          <OverviewPlaceholder aria-busy="true" aria-label="Reading the loan's records">
            <div className="journey" /><div className="step" /><div className="actions" /><div className="panel" />
          </OverviewPlaceholder>
        )}
        {activeTab === 'overview' && <div style={hiddenUntilReady}>
          <FacilityJourneyBar journey={facilityJourney} onNext={() => followNextStep(facilityJourney.next.target)} />
          {pcFacilityPage && <FacilityAttentionRail loanId={Number(loan.id)} refreshKey={refreshVersion} onOpen={openFromAttention} />}
          {renderOverview()}
        </div>}
        {activeTab === 'servicing' && pcFacilityPage && <ServicingWorkspace loanId={Number(loan.id)} refreshKey={refreshVersion} onAction={kind => setActionKind(kind)} />}
        {activeTab === 'agreement' && pcFacilityPage && <AgreementWorkspace loanId={Number(loan.id)} refreshKey={refreshVersion} />}
        {activeTab === 'documents' && pcFacilityPage && <DocumentsWorkspace loanId={Number(loan.id)} refreshKey={refreshVersion} />}
        {activeTab === 'activity' && pcFacilityPage && <ActivityWorkspace loanId={Number(loan.id)} refreshKey={refreshVersion} />}
        <CreditLoanOperatingTabs
          key={`${loan.source}:${loan.id}`}
          tab={activeTab} loan={loan} workspace={opsWorkspace} loading={opsLoading} loadError={opsError}
          borrower={borrower} checks={underwritingChecks} readiness={readinessScore}
          covenants={linkedCovenants} alerts={unqueuedLinkedAlerts} trades={linkedTrades}
          failedEvents={failedEvents} watchlist={linkedWatchlist} distressed={linkedDistressed} events={events}
          lmeScore={lmeScore} lmeSeverity={lmeSeverity} lmeDrivers={lmeDrivers}
          thresholdText={covenantThresholdText} actualText={covenantActualText}
          cushionText={covenantCushionText} cureText={covenantCureText}
          onSelectTab={selectTab} onRefresh={loadOpsWorkspace} exceptionSection={exceptionSection}
          agreementHref={sourceAgreementJobId ? agreementReviewPath(sourceAgreementJobId) : undefined}
          onCovenantCenter={() => onNavigate('covenants')} onWorkoutModule={openWorkoutModule}
          manualEvidence={!events.some(e => hasText(e.sourceDocumentName) && hasText(e.sourceJobId)) && (
            <AttestationPanel>
              <AttestationToggle><input type="checkbox" checked={manualEvidenceAttested}
                onChange={e => setManualEvidenceAttested(e.target.checked)} />Attest manual IC evidence</AttestationToggle>
              {manualEvidenceAttested && <AttestationInput aria-label="Manual evidence reference" value={manualEvidenceNote}
                onChange={e => setManualEvidenceNote(e.target.value)} placeholder="Evidence reference" />}
              <RowSubtext>{manualEvidenceAttested && !manualEvidenceNote.trim()
                ? 'Enter an evidence reference to complete the attestation. Do this before opening the packet editor.'
                : 'Save the IC packet to record this attestation.'}</RowSubtext>
            </AttestationPanel>
          )}
        />

        {actionKind && (
          <CreditActionModal
            kind={actionKind}
            assetKind={loan.source === 'LEVERAGED_LOAN' ? 'LL' : 'PC'}
            position={lifecyclePosition}
            initial={actionInitial}
            onClose={() => { setActionKind(null); setActionInitial(undefined); }}
            onSuccess={() => {
              setActionKind(null);
              setRefreshVersion(value => value + 1);
              onDataChanged?.();
            }}
          />
        )}
    </>
  );

  if (pcFacilityPage) {
    const attentionState = opsError || eventsError ? 'Unavailable' : opsLoading || eventsLoading ? 'Loading'
      : openExceptionCount === 0 ? 'Clear' : openPersistedExceptions.length > 0 ? String(openPersistedExceptions.length) : String(signalCount);
    const pendingPricing = loan.pendingPricingEffectiveDate
      ? `${loan.pendingSpreadBps != null ? `${loan.pendingSpreadBps} bps` : 'new pricing'} from ${fmtDate(loan.pendingPricingEffectiveDate)}` : null;
    const facts: ShellFact[] = [
      { label: <>Commitment <ExplainMetricButton scope="current" metric="COMMITMENT" /></>, value: moneyFull(loan.currentCommitment ?? loan.originalCommitment),
        sub: `${plainCode(loan.facilityType) || 'facility'} · recorded on the facility` },
      { label: <>Funded <ExplainMetricButton scope="current" metric="FUNDED" /></>, value: moneyFull(loan.fundedAmount),
        sub: walkCommitment > 0 ? `${utilizationPct != null ? `${utilizationPct.toFixed(0)}% drawn · ` : ''}official position` : 'official position' },
      { label: <>Unfunded <ExplainMetricButton scope="current" metric="UNFUNDED" /></>, value: moneyFull(loan.unfundedAmount ?? loan.ddtlUndrawn),
        sub: loan.ddtlExpiry ? `DDTL availability to ${fmtDate(loan.ddtlExpiry)}` : 'available to draw' },
      { label: <>All-in rate <ExplainMetricButton scope="current" metric="ALL_IN_RATE" /></>, value: loan.allInRate != null ? percent(loan.allInRate) : 'Not recorded',
        sub: pendingPricing || (loan.spreadBps != null ? `${loan.baseRate || 'base'} + ${loan.spreadBps} bps` : 'spread not recorded') },
      { label: 'Maturity', value: fmtDate(loan.maturityDate), sub: loan.closingDate ? `closed ${fmtDate(loan.closingDate)}` : plainCode(loan.status) || '' },
      { label: 'Covenants', value: linkedCovenants.length ? `${linkedCovenants.length} defined` : 'None defined',
        sub: unqueuedLinkedAlerts.length ? `${unqueuedLinkedAlerts.length} alert${unqueuedLinkedAlerts.length === 1 ? '' : 's'} to review` : 'no alert to review',
        onClick: () => selectTab('covenants') },
      // F-PCP-012: on a private credit facility the header counts what the board, rail and work queue count — the
      // attention service's open items — not the legacy exception/signal tally; the click opens the queue on this loan.
      pcFacilityPage && pcAttention
        ? { label: <>Attention <ExplainMetricButton scope="current" metric="ATTENTION" /></>,
            value: pcAttention.open === 0 ? 'Clear' : `${pcAttention.open} open${pcAttention.critical ? ` · ${pcAttention.critical} critical` : ''}`,
            tone: pcAttention.open === 0 ? 'emerald' : pcAttention.critical ? 'rose' : 'amber',
            sub: pcAttention.open === 0 ? 'nothing waits on this facility' : `${pcAttention.workflows}; ${openPersistedExceptions.length} recorded exception${openPersistedExceptions.length === 1 ? '' : 's'}`,
            onClick: () => navigate(`${PC_QUEUE_PATH}?loanId=${loan.id}`) }
        : { label: <>Attention <ExplainMetricButton scope="current" metric="ATTENTION" /></>, value: attentionState,
        tone: attentionState === 'Clear' ? 'emerald' : ['Loading', 'Unavailable'].includes(attentionState) ? 'default' : 'amber',
        sub: openExceptionCount === 0 ? 'no open exception or signal' : attentionBreakdown, onClick: () => { setExceptionSection('queue'); selectTab('exceptions'); } },
      { label: 'Next best action', value: facilityJourney.next.action || 'Nothing to do', sub: facilityJourney.next.headline,
        tone: facilityJourney.next.action ? 'amber' : 'emerald', title: facilityJourney.next.reason,
        onClick: facilityJourney.next.action ? () => followNextStep(facilityJourney.next.target) : undefined },
    ];
    return (
      <FacilityShell facilityLabel="Private credit facility"
        back={{ label: 'Private credit', onClick: () => navigate(loanListPath(loan.source)) }}
        title={loan.loanName || borrower}
        meta={<>{borrower}{loan.sponsorName ? ` · ${loan.sponsorName}` : ''} · {plainCode(loan.facilityType) || 'facility pending'} · {loan.loanId || 'identifier pending'}{loan.arrangement?.arrangementName ? ` · ${loan.arrangement.arrangementName}` : ''} · {plainCode(loan.status) || '--'}</>}
        actions={<>
          {sourceAgreementJobId && <WorkspaceButton title="Every term of the agreement beside the passage it was read from" onClick={() => navigate(agreementReviewPath(sourceAgreementJobId))} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><FileSearch size={15} /> Agreement &amp; sources</WorkspaceButton>}
          <WorkspaceButton onClick={() => selectTab('underwriting')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><FileText size={15} /> Underwriting</WorkspaceButton>
          {loanCusip && <WorkspaceButton onClick={() => navigate(blotterLoanPath(loanCusip))}>Tickets in Blotter</WorkspaceButton>}
        </>}
        facts={facts}
        tabs={tabs.map(tab => ({ id: tab.id, label: tab.id === 'exceptions' ? 'Exceptions' : tab.label, count: tab.count,
          tone: tab.id === 'lme' ? (lmeTone === 'red' ? 'red' : lmeTone === 'amber' ? 'amber' : 'neutral')
            : Number(tab.count) > 0 || (tab.id === 'exceptions' && signalCount > 0) ? 'amber' : 'emerald' }))}
        activeTab={activeTab} onTab={id => selectTab(id as DrawerTabKey)}>
        {timelineWarning}
        {content}
      </FacilityShell>
    );
  }

  if (isFacilityPage) {
    return (
      <CreditFacilityPage facilityLabel={`${sourceLabel(loan.source)} facility`} header={header}
        stickyHeader={false}
        metrics={loan.source === 'LEVERAGED_LOAN' && ['overview', 'settlement'].includes(activeTab) ? null : renderMetrics()} navigation={navigation}>
        {timelineWarning}
        {content}
      </CreditFacilityPage>
    );
  }

  return (
    <DrawerBackdrop onClick={onClose}>
      <ControlRoomDrawer onClick={e => e.stopPropagation()}>
        {header}
        {timelineWarning}
        {navigation}
        {content}
      </ControlRoomDrawer>
    </DrawerBackdrop>
  );
};

export default Loan360Drawer;
